import './AttributeMappingModal.scss';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlightButton,
  FlightModal,
  FlightRadioButton,
  FlightSelect,
  FlightSuggestionList,
  FlightTextInput,
  FlightToggleSwitch,
  getIcon,
} from '@flybits/design-system';
import { usePortalContainer } from 'components/Shared/LargeModal/PortalContainerContext';
import { capitalizeFirstCharacter } from 'helpers/common.helper';
import usePlugins, { TContextAttribute, TContextPlugin, TDefaultValueSettings } from 'hooks/useContextPlugins';
import { cloneDeep } from 'lodash';
import { TAttributeParameter } from 'components/FlowBuilder/FlowSidePanels/types';

const MAIN_CLASS = 'attribute-mapping-modal';
const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  HEADER_TITLE: `${MAIN_CLASS}__header__title`,
  HEADER_ATTRIBUTE_SELECTOR: `${MAIN_CLASS}__header__attribute-selector`,
  CONTENT: `${MAIN_CLASS}__content`,
  CONTENT_HEADING: `${MAIN_CLASS}__content__heading`,
  CONTENT_HEADING_ICON: `${MAIN_CLASS}__content__heading__icon`,
  CONTENT_HEADING_PLUGIN: `${MAIN_CLASS}__content__heading__plugin`,
  CONTENT_BODY: `${MAIN_CLASS}__content__body`,
  CONTENT_BODY_INFO: `${MAIN_CLASS}__content__body__info`,
  CONTENT_BODY_INFO_ATTRIBUTE: `${MAIN_CLASS}__content__body__info__attribute`,
  CONTENT_BODY_INFO_ATTRIBUTE_NAME: `${MAIN_CLASS}__content__body__info__attribute__name`,
  CONTENT_BODY_INFO_ATTRIBUTE_DETAILS: `${MAIN_CLASS}__content__body__info__attribute__details`,
  CONTENT_BODY_INFO_ATTRIBUTE_DETAILS_LABEL: `${MAIN_CLASS}__content__body__info__attribute__details__label`,
  CONTENT_BODY_INFO_ATTRIBUTE_DETAILS_VALUE: `${MAIN_CLASS}__content__body__info__attribute__details__value`,
  CONTENT_BODY_KEYS: `${MAIN_CLASS}__content__body__keys`,
  CONTENT_BODY_KEYS_HEADING: `${MAIN_CLASS}__content__body__keys__heading`,
  CONTENT_BODY_KEYS_HEADING_INFO: `${MAIN_CLASS}__content__body__keys__heading__info`,
  CONTENT_BODY_KEYS_HEADING_INFO_TITLE: `${MAIN_CLASS}__content__body__keys__heading__info__title`,
  CONTENT_BODY_KEYS_HEADING_INFO_DESCRIPTION: `${MAIN_CLASS}__content__body__keys__heading__info__description`,
  CONTENT_BODY_KEYS_OPTIONS: `${MAIN_CLASS}__content__body__keys__options`,
  CONTENT_BODY_PARAMETERS: `${MAIN_CLASS}__content__body__parameters`,
  CONTENT_BODY_PARAMETERS_HEADING: `${MAIN_CLASS}__content__body__parameters__heading`,
  CONTENT_BODY_PARAMETERS_HEADING_INFO: `${MAIN_CLASS}__content__body__parameters__heading__info`,
  CONTENT_BODY_PARAMETERS_HEADING_INFO_TITLE: `${MAIN_CLASS}__content__body__parameters__heading__info__title`,
  CONTENT_BODY_PARAMETERS_HEADING_INFO_DESCRIPTION: `${MAIN_CLASS}__content__body__parameters__heading__info__description`,
  CONTENT_BODY_PARAMETERS_HEADING_BANNER: `${MAIN_CLASS}__content__body__parameters__heading__banner`,
  CONTENT_BODY_PARAMETERS_HEADING_BANNER_ICON: `${MAIN_CLASS}__content__body__parameters__heading__banner__icon`,
  CONTENT_BODY_PARAMETERS_HEADING_BANNER_TEXT: `${MAIN_CLASS}__content__body__parameters__heading__banner__text`,
  CONTENT_BODY_PARAMETERS_LIST: `${MAIN_CLASS}__content__body__parameters__list`,
  CONTENT_BODY_PARAMETERS_LIST_ITEM: `${MAIN_CLASS}__content__body__parameters__list__item`,
  CONTENT_BODY_PARAMETERS_LIST_ITEM_NAME: `${MAIN_CLASS}__content__body__parameters__list__item__name`,
  CONTENT_BODY_PARAMETERS_LIST_ITEM_VALUE: `${MAIN_CLASS}__content__body__parameters__list__item__value`,
  CONTENT_BODY_PARAMETERS_LIST_ITEM_VALUE_DEFAULT: `${MAIN_CLASS}__content__body__parameters__list__item__value__default`,
  CONTENT_BODY_PARAMETERS_EMPTY: `${MAIN_CLASS}__content__body__parameters--empty`,
  CONTENT_BODY_PARAMETERS_EMPTY_ICON: `${MAIN_CLASS}__content__body__parameters--empty__icon`,
  CONTENT_BODY_PARAMETERS_EMPTY_TEXT: `${MAIN_CLASS}__content__body__parameters--empty__text`,
  CONTENT_BODY_INCOMING_CONTEXT: `${MAIN_CLASS}__content__body__incoming-context`,
  CONTENT_BODY_INCOMING_CONTEXT_INFO: `${MAIN_CLASS}__content__body__incoming-context__info`,
  CONTENT_BODY_INCOMING_CONTEXT_INFO_TITLE: `${MAIN_CLASS}__content__body__incoming-context__info__title`,
  CONTENT_BODY_INCOMING_CONTEXT_INFO_DESCRIPTION: `${MAIN_CLASS}__content__body__incoming-context__info__description`,
  CONTENT_EMPTY: `${MAIN_CLASS}__content--empty`,
  CONTENT_EMPTY_ICON: `${MAIN_CLASS}__content--empty__icon`,
  CONTENT_EMPTY_TEXT: `${MAIN_CLASS}__content--empty__text`,
  FOOTER: `${MAIN_CLASS}__footer`,
  REMOVE_ATTRIBUTE_BUTTON: `${MAIN_CLASS}__footer__remove-attribute-button`,
};

type TAttributeOption = TContextAttribute & {
  name: string;
  subText: string[];
  id: string;
};

type TModalHeaderProps = {
  allAttributes: TAttributeOption[];
  selectedAttribute: TAttributeOption | null;
  onAttributeSelect: (attribute: TAttributeOption | null) => void;
  tagName: string;
  title?: string;
};

function ModalHeader({ allAttributes, selectedAttribute, onAttributeSelect, tagName, title }: TModalHeaderProps) {
  const portalContainer = usePortalContainer();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter attribute options by search term
  const filteredOptions = useMemo(
    () =>
      !searchTerm
        ? allAttributes
        : allAttributes.filter((option) => option.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [allAttributes, searchTerm],
  );

  const handleSearch = useCallback((term: string) => setSearchTerm(term), [setSearchTerm]);

  // Props for FlightSuggestionList
  const ctxAttributesListProps = useMemo(
    () => ({
      ariaControlsId: 'attribute-selector',
      width: '814px',
      options: filteredOptions,
      selected: selectedAttribute,
      searchPlaceholder: 'search for a context attribute',
      intro: { icon: '', text: '' },
      content: {
        noDataAvailableMsg: { icon: 'search', text: 'No context plugin available' },
        serverErrorMsg: {
          icon: 'warning',
          text: 'Could not load plugins list. Please refresh or contact support.',
        },
      },
      loading: false,
      noDataAvailable: false,
      isServerError: false,
      hasClearButton: true,
      onClear: () => {
        onAttributeSelect(null);
      },
      handleOptionClick: onAttributeSelect,
      handleSearch,
      portalContainer,
    }),
    [filteredOptions, selectedAttribute, onAttributeSelect, handleSearch, portalContainer],
  );

  return (
    <div className={CLASSES.HEADER}>
      <div className={CLASSES.HEADER_TITLE}>
        {title ? title : selectedAttribute?.name ? `Mapping context plugin to {{${tagName}}}` : 'Select a plugin'}
      </div>
      <div className={CLASSES.HEADER_ATTRIBUTE_SELECTOR}>
        {/* TODO: fix design-system typing using generics*/}
        <FlightSuggestionList {...(ctxAttributesListProps as React.ComponentProps<typeof FlightSuggestionList>)} />
      </div>
    </div>
  );
}

type DefaultParameterBlockProps = {
  selectedAttributeOption: TAttributeOption | null;
  selectedAttributeDefaultValues: TDefaultValueSettings | undefined;
  setDefaultValueHasError?: (hasError: boolean) => void;
  setDefaultValue?: (value: string) => void;
  disabled?: boolean;
};

// Boolean dropdown options (top-level constant)
const BOOLEAN_OPTIONS = [
  { key: '', name: 'Select An Option' },
  { key: 'true', name: 'True' },
  { key: 'false', name: 'False' },
];

const DefaultParameterBlock = ({
  selectedAttributeOption,
  selectedAttributeDefaultValues,
  setDefaultValueHasError,
  setDefaultValue,
  disabled = false,
}: DefaultParameterBlockProps) => {
  const portalContainer = usePortalContainer();

  // Extract type and initial value
  const defaultValueType = useMemo(() => selectedAttributeOption?.subText?.[0] ?? null, [selectedAttributeOption]);

  // Determine if type is boolean or map[string]bool/boolean
  const isBooleanType = useMemo(() => {
    if (typeof defaultValueType !== 'string') return false;
    const typeStr = defaultValueType.toLowerCase();
    if (typeStr === 'boolean' || typeStr === 'bool') return true;
    if (typeStr.startsWith('map[')) {
      return /^map\[string\](bool|boolean)$/.test(typeStr);
    }
    return false;
  }, [defaultValueType]);

  // Compute initial default value
  const initialDefault = useMemo(() => {
    let val = selectedAttributeDefaultValues?.defaultValue?.value;
    if (typeof defaultValueType === 'string' && defaultValueType.toLowerCase().startsWith('map[') && val) {
      let parsed = val;
      if (typeof val === 'string') {
        try {
          parsed = JSON.parse(val);
        } catch {
          // fallback: treat as plain string
        }
      }
      if (parsed && typeof parsed === 'object' && parsed !== null) {
        const firstKey = Object.keys(parsed)[0];
        val = parsed[firstKey];
      }
    }
    val = normalizeBoolean(val);
    if (isBooleanType && (val === '' || val === null || val === undefined)) {
      val = '';
    }
    return val ?? '';
  }, [selectedAttributeDefaultValues, isBooleanType, defaultValueType]);

  // State for input and validation
  const [inputValue, setInputValue] = useState(initialDefault);
  const [isValid, setIsValid] = useState(() => validateDefaultValue(initialDefault, defaultValueType));

  // Update inputValue and validation when default changes
  useEffect(() => {
    setInputValue(initialDefault);
    setIsValid(
      initialDefault === '' || initialDefault === null ? true : validateDefaultValue(initialDefault, defaultValueType),
    );
    if (setDefaultValue) setDefaultValue(initialDefault);
  }, [initialDefault, defaultValueType, setDefaultValue]);

  // Boolean dropdown handler
  const handleBooleanChange = useCallback(
    (option: { key: string; name: string }) => {
      setInputValue(option.key);
      const valid = option.key === '' ? true : validateDefaultValue(option.key, defaultValueType);
      setIsValid(valid);
      if (setDefaultValueHasError) setDefaultValueHasError(!valid);
      if (setDefaultValue) setDefaultValue(option.key);
    },
    [defaultValueType, setDefaultValueHasError, setDefaultValue],
  );

  // Text input handler
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      const isBlank = value === '';
      setIsValid(isBlank ? true : validateDefaultValue(value, defaultValueType));
      if (setDefaultValueHasError) {
        setDefaultValueHasError(isBlank ? false : !validateDefaultValue(value, defaultValueType));
      }
      if (setDefaultValue) setDefaultValue(value);
    },
    [defaultValueType, setDefaultValueHasError, setDefaultValue],
  );

  // Render FlightDropdown for boolean, else FlightTextInput
  return (
    <div className={`${CLASSES.CONTENT_BODY_PARAMETERS} default-parameter`}>
      <div className={CLASSES.CONTENT_BODY_PARAMETERS_LIST}>
        <div className={CLASSES.CONTENT_BODY_PARAMETERS_LIST_ITEM}>
          <div className={`${CLASSES.CONTENT_BODY_PARAMETERS_LIST_ITEM_NAME} default-parameter `}>
            Default value (optional)
          </div>
          <div className={CLASSES.CONTENT_BODY_PARAMETERS_LIST_ITEM_VALUE_DEFAULT}>
            {isBooleanType ? (
              <FlightSelect
                options={BOOLEAN_OPTIONS}
                selected={BOOLEAN_OPTIONS.find((opt) => opt.key === normalizeBoolean(inputValue)) || BOOLEAN_OPTIONS[0]}
                handleOptionClick={handleBooleanChange}
                hasError={!disabled && !isValid && normalizeBoolean(inputValue) !== ''}
                portalContainer={portalContainer}
                disabled={disabled}
              />
            ) : (
              <FlightTextInput
                value={inputValue}
                onChange={handleInputChange}
                hasError={!disabled && !isValid}
                errorMessage={
                  disabled || isValid ? '' : `Invalid default value. This field must be of type ${defaultValueType}`
                }
                placeholderText={'Enter a value'}
                trailingIcon={' '}
                disabled={disabled}
              />
            )}
            {!disabled && isValid && <div>This will be used only if no matching results are found</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

type IncomingContextAndDefaultBlockProps = DefaultParameterBlockProps & {
  useIncomingContext: boolean;
  onUseIncomingContextChange: (value: boolean) => void;
};

// Renders the "Use incoming context" toggle above the default value field. When the toggle is on,
// the mapping should draw from the transient context of the triggering request, so a default value
// cannot be provided and the default value field is disabled.
const IncomingContextAndDefaultBlock = ({
  useIncomingContext,
  onUseIncomingContextChange,
  ...defaultParameterBlockProps
}: IncomingContextAndDefaultBlockProps) => {
  return (
    <>
      <div className={CLASSES.CONTENT_BODY_INCOMING_CONTEXT}>
        <div className={CLASSES.CONTENT_BODY_INCOMING_CONTEXT_INFO}>
          <div className={CLASSES.CONTENT_BODY_INCOMING_CONTEXT_INFO_TITLE}>Use incoming context</div>
          <div className={CLASSES.CONTENT_BODY_INCOMING_CONTEXT_INFO_DESCRIPTION}>
            Use transient context data captured from the request that triggered this action. When enabled, a default
            value cannot be set.
          </div>
        </div>
        <FlightToggleSwitch
          label={useIncomingContext ? 'Enabled' : 'Disabled'}
          checked={useIncomingContext}
          onChange={() => onUseIncomingContextChange(!useIncomingContext)}
        />
      </div>
      <DefaultParameterBlock {...defaultParameterBlockProps} disabled={useIncomingContext} />
    </>
  );
};

type TModalContentProps = {
  selectedAttributeOption: TAttributeOption | null;
  attributeKeys?: string[];
  selectedAttributeKey?: string;
  onSelectedAttributeKeyChange?: (key: string) => void;
  areParametersEmpty: boolean;
  selectedAttributeParameters: TAttributeParameter[];
  selectedAttributeDefaultValues: TDefaultValueSettings | undefined;
  onParameterChange: (index: number, value?: string, option?: { key: string; name: string }) => void;
  setDefaultValueHasError?: (hasError: boolean) => void;
  setDefaultValue?: (value: string) => void;
  useIncomingContext: boolean;
  onUseIncomingContextChange: (value: boolean) => void;
};

function ModalContent({
  selectedAttributeOption,
  attributeKeys,
  selectedAttributeKey,
  onSelectedAttributeKeyChange,
  areParametersEmpty,
  selectedAttributeParameters,
  selectedAttributeDefaultValues,
  onParameterChange,
  setDefaultValueHasError,
  setDefaultValue,
  useIncomingContext,
  onUseIncomingContextChange,
}: TModalContentProps) {
  const portalContainer = usePortalContainer();
  const selectedAttributeOptionName = selectedAttributeOption?.name.trim() || '';
  const selectedPluginName = selectedAttributeOptionName?.split('/')[0] || '';
  const selectedAttributeName = selectedAttributeOptionName?.split('/')[1] || '';
  const selectedAttributeType = selectedAttributeOption?.subText?.[0] || '';
  const selectedAttributeId = selectedAttributeOption?.subText?.[1] || '';

  return (
    <div className={CLASSES.CONTENT}>
      {selectedAttributeOption ? (
        <>
          <div className={CLASSES.CONTENT_HEADING}>
            <div className={CLASSES.CONTENT_HEADING_ICON}>{getIcon('person', {})}</div>
            <div className={CLASSES.CONTENT_HEADING_PLUGIN}>
              {selectedPluginName ? capitalizeFirstCharacter(selectedPluginName) : 'Select an attribute to map'}
            </div>
          </div>
          <div className={CLASSES.CONTENT_BODY}>
            <div className={CLASSES.CONTENT_BODY_INFO}>
              <div className={CLASSES.CONTENT_BODY_INFO_ATTRIBUTE}>
                <div className={CLASSES.CONTENT_BODY_INFO_ATTRIBUTE_NAME}>
                  {selectedAttributeName ? selectedAttributeName : 'No attribute selected'}
                </div>
                <div className={CLASSES.CONTENT_BODY_INFO_ATTRIBUTE_DETAILS}>
                  <div className={CLASSES.CONTENT_BODY_INFO_ATTRIBUTE_DETAILS_LABEL}>
                    {selectedAttributeType || 'N/A'}
                  </div>
                  <div className={CLASSES.CONTENT_BODY_INFO_ATTRIBUTE_DETAILS_VALUE}>
                    {selectedAttributeId || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            {selectedAttributeName && attributeKeys && attributeKeys.length > 0 && (
              <div className={CLASSES.CONTENT_BODY_KEYS}>
                <div className={CLASSES.CONTENT_BODY_KEYS_HEADING}>
                  <div className={CLASSES.CONTENT_BODY_KEYS_HEADING_INFO}>
                    <div className={CLASSES.CONTENT_BODY_KEYS_HEADING_INFO_TITLE}>Pick a key</div>
                    <div className={CLASSES.CONTENT_BODY_KEYS_HEADING_INFO_DESCRIPTION}>
                      {`${selectedAttributeName} has ${attributeKeys.length} keys. Choose which one to insert.`}
                    </div>
                  </div>
                </div>
                <div className={CLASSES.CONTENT_BODY_KEYS_OPTIONS}>
                  {attributeKeys.map((key) => (
                    <FlightRadioButton
                      key={key}
                      label={key}
                      value={key}
                      checked={key === selectedAttributeKey}
                      onSelect={onSelectedAttributeKeyChange}
                    />
                  ))}
                </div>
              </div>
            )}
            {!areParametersEmpty ? (
              <div className={CLASSES.CONTENT_BODY_PARAMETERS}>
                <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING}>
                  <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING_INFO}>
                    <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING_INFO_TITLE}>Manage parameter(s)</div>
                    <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING_INFO_DESCRIPTION}>
                      Some plugins need extra input called parameters to know exactly what data to return. Add a value
                      to the required parameters below to ensure your message works correctly.
                    </div>
                  </div>
                  <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING_BANNER}>
                    <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING_BANNER_ICON}>
                      {getIcon('infoFilled', {})}
                    </div>
                    <div className={CLASSES.CONTENT_BODY_PARAMETERS_HEADING_BANNER_TEXT}>
                      Required field(s). Please add value.
                    </div>
                  </div>
                </div>
                <div className={CLASSES.CONTENT_BODY_PARAMETERS_LIST}>
                  {selectedAttributeParameters.map((param, index) => (
                    <div key={`${index}-${param.name}`} className={CLASSES.CONTENT_BODY_PARAMETERS_LIST_ITEM}>
                      <div className={CLASSES.CONTENT_BODY_PARAMETERS_LIST_ITEM_NAME}>
                        {param.name}&nbsp;<span className="required-asterisk">*</span>
                      </div>
                      <div className={CLASSES.CONTENT_BODY_PARAMETERS_LIST_ITEM_VALUE}>
                        {param.options?.length ? (
                          <FlightSelect
                            options={param.options}
                            selected={param.options.find((o) => o.key === param.value)}
                            handleOptionClick={(option: { key: string; name: string }) =>
                              onParameterChange(index, '', option)
                            }
                            label={'Select a value'}
                            hasError={param.value === ''}
                            portalContainer={portalContainer}
                          />
                        ) : (
                          <FlightTextInput
                            value={param.value}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => onParameterChange(index, e.target.value)}
                            placeholderText={'Enter a value'}
                            hasError={param.value === ''}
                            trailingIcon={' '}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <IncomingContextAndDefaultBlock
                  selectedAttributeOption={selectedAttributeOption}
                  selectedAttributeDefaultValues={selectedAttributeDefaultValues}
                  setDefaultValueHasError={setDefaultValueHasError}
                  setDefaultValue={setDefaultValue}
                  useIncomingContext={useIncomingContext}
                  onUseIncomingContextChange={onUseIncomingContextChange}
                />
              </div>
            ) : (
              <>
                <div className={`${CLASSES.CONTENT_BODY_PARAMETERS} ${CLASSES.CONTENT_BODY_PARAMETERS_EMPTY}`}>
                  <div className={CLASSES.CONTENT_BODY_PARAMETERS_EMPTY_ICON}>{getIcon('itemsList', {})}</div>
                  <div className={CLASSES.CONTENT_BODY_PARAMETERS_EMPTY_TEXT}>
                    If parameters exist for a context plugin they will appear here
                  </div>
                </div>
                <IncomingContextAndDefaultBlock
                  selectedAttributeOption={selectedAttributeOption}
                  selectedAttributeDefaultValues={selectedAttributeDefaultValues}
                  setDefaultValueHasError={setDefaultValueHasError}
                  setDefaultValue={setDefaultValue}
                  useIncomingContext={useIncomingContext}
                  onUseIncomingContextChange={onUseIncomingContextChange}
                />
              </>
            )}
          </div>
        </>
      ) : (
        <div className={`${CLASSES.CONTENT} ${CLASSES.CONTENT_EMPTY}`}>
          <div className={CLASSES.CONTENT_EMPTY_ICON}>{getIcon('puzzle', {})}</div>
          <div className={CLASSES.CONTENT_EMPTY_TEXT}>Select a context plugin</div>
        </div>
      )}
    </div>
  );
}

type TModalFooterProps = {
  onClose: () => void;
  onApply: () => void;
  isApplyDisabled: boolean;
  onRemoveAttribute?: () => void;
  showRemoveAttributeButton?: boolean;
  isRemoveAttributeButtonDisabled?: boolean;
  primaryButtonLabel?: string;
};

function ModalFooter({
  onClose,
  onApply,
  isApplyDisabled,
  onRemoveAttribute,
  showRemoveAttributeButton,
  isRemoveAttributeButtonDisabled,
  primaryButtonLabel,
}: TModalFooterProps) {
  return (
    <div className={CLASSES.FOOTER}>
      {showRemoveAttributeButton && (
        <FlightButton
          className={CLASSES.REMOVE_ATTRIBUTE_BUTTON}
          theme="secondary"
          onClick={() => {
            onRemoveAttribute?.();
            onClose();
          }}
          label="Remove attribute"
          disabled={isRemoveAttributeButtonDisabled}
        />
      )}
      <FlightButton theme="secondary" onClick={onClose} label="Cancel" />
      <FlightButton
        theme="primary"
        onClick={() => {
          onApply();
          onClose();
        }}
        label={primaryButtonLabel || 'Apply'}
        disabled={isApplyDisabled}
      />
    </div>
  );
}

type TAttributeMappingModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onApply: (
    tagName: string,
    attributeId: string,
    parameters: TAttributeParameter[],
    defaultValue: string,
    selectedKey?: string,
    useIncomingContext?: boolean,
  ) => void;
  tagName: string;
  attributeId: string;
  attributeMapKey?: string;
  attributeParameters?: TAttributeParameter[] | null;
  attributeDefaultValue?: string;
  useIncomingContext?: boolean;
  title?: string;
  primaryButtonLabel?: string;
  onRemoveAttribute?: () => void;
  showRemoveAttributeButton?: boolean;
};

function AttributeMappingModal({
  isVisible,
  onClose,
  onApply,
  tagName,
  attributeId,
  attributeMapKey,
  attributeParameters,
  attributeDefaultValue,
  useIncomingContext: initialUseIncomingContext,
  onRemoveAttribute,
  showRemoveAttributeButton,
  title,
  primaryButtonLabel,
}: TAttributeMappingModalProps) {
  const { plugins }: { plugins: TContextPlugin[] } = usePlugins();

  const [selectedAttributeOption, setSelectedAttributeOption] = useState<TAttributeOption | null>(null);
  const [attributeKeys, setAttributeKeys] = useState<string[]>([]);
  const [selectedAttributeKey, setSelectedAttributeKey] = useState('');
  const [selectedAttributeParameters, setSelectedAttributeParameters] = useState<TAttributeParameter[]>([]);
  const [selectedAttributeDefaultValues, setSelectedAttributeDefaultValues] = useState<
    TDefaultValueSettings | undefined
  >(undefined);
  const [defaultValueHasError, setDefaultValueHasError] = useState(false);
  const [defaultValue, setDefaultValue] = useState<string>('');
  const [useIncomingContext, setUseIncomingContext] = useState<boolean>(!!initialUseIncomingContext);

  const handleUseIncomingContextChange = useCallback(
    (value: boolean) => {
      setUseIncomingContext(value);
      // Enabling incoming context clears any default value the user may have entered,
      // since the two are mutually exclusive.
      if (value) {
        setDefaultValue('');
        setSelectedAttributeDefaultValues(undefined);
        setDefaultValueHasError(false);
      }
    },
    [setDefaultValue, setSelectedAttributeDefaultValues, setDefaultValueHasError],
  );

  const handleOptionClick = useCallback(
    (option: TAttributeOption | null) => {
      if (option === null) {
        setSelectedAttributeOption(null);
        setSelectedAttributeParameters([]);
        setUseIncomingContext(false);
        return;
      }
      if (selectedAttributeOption?.uid === option?.uid) return;
      setSelectedAttributeOption(option);
      setSelectedAttributeParameters([]);
      setUseIncomingContext(false);
    },
    [selectedAttributeOption, setSelectedAttributeOption, setSelectedAttributeParameters],
  );

  const handleParameterChange = (index: number, value?: string, option?: { key: string; name: string }) => {
    const newParameters = cloneDeep(selectedAttributeParameters);
    if (option) {
      newParameters[index].value = option.key;
    } else {
      newParameters[index].value = value ?? '';
    }

    setSelectedAttributeParameters(newParameters);
  };

  const handleApply = () => {
    if (selectedAttributeOption) {
      onApply(
        tagName,
        selectedAttributeOption.id,
        selectedAttributeParameters,
        useIncomingContext ? '' : defaultValue,
        selectedAttributeKey,
        useIncomingContext,
      );
    }
  };

  const allAttributes = useMemo<TAttributeOption[]>(() => {
    if (!plugins || plugins.length === 0) {
      return [];
    }
    // Only sort and map if plugins change
    return plugins
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id))
      .flatMap((plugin) =>
        Object.entries(plugin.values).map(([attributeName, attribute]) => ({
          ...attribute,
          name: `${plugin.name} / ${attributeName}`,
          subText: [capitalizeFirstCharacter(attribute.valueType), `{{${attribute.id}}}`],
          id: attribute.uid,
        })),
      );
  }, [plugins]);

  // Enables the apply button based on selected attribute and parameters
  const isApplyDisabled = useMemo(() => {
    if (!selectedAttributeOption) {
      return true;
    }

    // If there are parameters, all must be filled
    if (selectedAttributeParameters.length > 0) {
      const allParamsFilled = selectedAttributeParameters.every((param) => param.value && param.value.trim() !== '');
      // For boolean default value, allow empty string ("Select An Option")
      const defaultValueType = selectedAttributeOption?.subText?.[0] ?? null;
      if (isBooleanType(defaultValueType)) {
        // Only disable if required params are not filled
        return !allParamsFilled;
      }
      // Incoming context disables the default value field, so its validation no longer applies
      return !allParamsFilled || (!useIncomingContext && defaultValueHasError);
    }

    // For boolean default value, allow empty string ("Select An Option")
    const defaultValueType = selectedAttributeOption?.subText?.[0] ?? null;
    if (isBooleanType(defaultValueType)) {
      return false;
    }

    // Incoming context disables the default value field, so its validation no longer applies
    return !useIncomingContext && defaultValueHasError;
  }, [defaultValueHasError, selectedAttributeOption, selectedAttributeParameters, useIncomingContext]);

  // Sets the selected attribute option based on the initial `attributeId`
  useEffect(() => {
    if (!attributeId || allAttributes.length === 0) {
      setSelectedAttributeOption(null);
      return;
    }

    const foundAttribute = allAttributes.find((attributeOption) => attributeOption.uid === attributeId);
    setSelectedAttributeOption(foundAttribute || null);
  }, [attributeId, allAttributes]);

  // Initializes or updates the attribute keys and selected key based on the selected attribute option
  useEffect(() => {
    if (!selectedAttributeOption) {
      setAttributeKeys([]);
      setSelectedAttributeKey('');
      return;
    }

    setAttributeKeys(selectedAttributeOption.valueKeys || []);
    setSelectedAttributeKey(attributeMapKey || '');
  }, [selectedAttributeOption, attributeMapKey, allAttributes]);

  // Initializes or updates the default values for the selected attribute
  useEffect(() => {
    if (selectedAttributeOption?.uid === attributeId && attributeDefaultValue) {
      setSelectedAttributeDefaultValues({
        defaultValue: { value: attributeDefaultValue },
      });
    } else if (selectedAttributeOption?.defaultValueSettings) {
      setSelectedAttributeDefaultValues(selectedAttributeOption.defaultValueSettings);
    } else {
      // TODO: investigate if this case is needed - may get initialized to '' already
      setSelectedAttributeDefaultValues(undefined);
    }
  }, [selectedAttributeOption, attributeDefaultValue, attributeId]);

  // Initializes or updates the parameters for the selected attribute
  useEffect(() => {
    if (!selectedAttributeOption?.parameters?.length && !attributeParameters?.length) {
      setSelectedAttributeParameters([]);
      return;
    }

    if (selectedAttributeOption?.uid === attributeId && attributeParameters?.length) {
      setSelectedAttributeParameters(attributeParameters);
    } else if (selectedAttributeOption?.parameters) {
      const parameters = selectedAttributeOption.parameters.map((param) => ({
        name: param.key,
        type: param.valueType,
        options:
          param.valueTypeOptions?.map((option) => ({
            key: option.value,
            name: option.displayName,
          })) || [],
        value: '',
      }));
      setSelectedAttributeParameters(parameters);
    }
  }, [attributeId, attributeParameters, selectedAttributeOption]);

  return (
    <FlightModal
      className={MAIN_CLASS}
      size="large"
      isVisible={isVisible}
      scrollable={true}
      toggleModalShown={() => {
        setSelectedAttributeOption(null);
        onClose();
      }}
      header={
        <ModalHeader
          allAttributes={allAttributes}
          selectedAttribute={selectedAttributeOption}
          onAttributeSelect={handleOptionClick}
          tagName={tagName}
          title={title}
        />
      }
      content={
        <ModalContent
          selectedAttributeOption={selectedAttributeOption}
          attributeKeys={attributeKeys}
          selectedAttributeKey={selectedAttributeKey}
          onSelectedAttributeKeyChange={setSelectedAttributeKey}
          areParametersEmpty={selectedAttributeParameters.length === 0}
          selectedAttributeParameters={selectedAttributeParameters}
          selectedAttributeDefaultValues={selectedAttributeDefaultValues}
          onParameterChange={handleParameterChange}
          setDefaultValueHasError={setDefaultValueHasError}
          setDefaultValue={setDefaultValue}
          useIncomingContext={useIncomingContext}
          onUseIncomingContextChange={handleUseIncomingContextChange}
        />
      }
      footer={
        <ModalFooter
          onClose={() => {
            setSelectedAttributeOption(null);
            onClose();
          }}
          onApply={handleApply}
          isApplyDisabled={isApplyDisabled}
          onRemoveAttribute={onRemoveAttribute}
          showRemoveAttributeButton={showRemoveAttributeButton}
          primaryButtonLabel={primaryButtonLabel}
        />
      }
    />
  );
}

// Utility to check if a type is boolean
function isBooleanType(type: string | null | undefined): boolean {
  return typeof type === 'string' && (type.toLowerCase() === 'boolean' || type.toLowerCase() === 'bool');
}

// Utility to normalize boolean values
function normalizeBoolean(val: string | boolean | number | undefined | null): string {
  if (val === false) return 'false';
  if (val === true) return 'true';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val.trim().toLowerCase();
  return '';
}

// Validation logic
function validateDefaultValue(value: string, type: string | null): boolean {
  if (type === null) return true;
  // Handle map types
  if (typeof type === 'string' && type.toLowerCase().startsWith('map[')) {
    const match = type.match(/^map\[string\](bool|boolean|int|integer|float|double|number|string)$/i);
    if (!match) return false;
    const valueType = match[1].toLowerCase();
    // If value is primitive, validate as primitive
    if (typeof value !== 'object' || value === null) {
      if (valueType === 'bool' || valueType === 'boolean') {
        const normalized = String(value).trim().toLowerCase();
        return normalized === 'true' || normalized === 'false';
      }
      if (valueType === 'int' || valueType === 'integer') {
        return /^-?\d+$/.test(String(value));
      }
      if (valueType === 'float' || valueType === 'double' || valueType === 'number') {
        return /^-?\d*(\.\d+)?$/.test(String(value)) && !isNaN(Number(value));
      }
      if (valueType === 'string') {
        // Only non-empty strings are valid, numbers are not
        return typeof value === 'string' && value.length > 0 && isNaN(Number(value));
      }
      return false;
    }
    // If value is object, validate all values
    return Object.values(value).every((v) => {
      if (valueType === 'bool' || valueType === 'boolean') {
        return typeof v === 'boolean';
      }
      if (valueType === 'int' || valueType === 'integer') {
        return typeof v === 'number' && Number.isInteger(v);
      }
      if (valueType === 'float' || valueType === 'double' || valueType === 'number') {
        return typeof v === 'number';
      }
      if (valueType === 'string') {
        return typeof v === 'string' && v.length > 0 && isNaN(Number(v));
      }
      return false;
    });
  }
  // Handle primitive types
  if (typeof type === 'string' && (type.toLowerCase() === 'boolean' || type.toLowerCase() === 'bool')) {
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'true' || normalized === 'false';
  }
  if (typeof type === 'string' && (type.toLowerCase() === 'integer' || type.toLowerCase() === 'int')) {
    return /^-?\d+$/.test(String(value));
  }
  if (
    typeof type === 'string' &&
    (type.toLowerCase() === 'float' || type.toLowerCase() === 'double' || type.toLowerCase() === 'number')
  ) {
    return /^-?\d*(\.\d+)?$/.test(String(value)) && !isNaN(Number(value));
  }
  if (typeof type === 'string' && type.toLowerCase() === 'string') {
    return String(value).length > 0;
  }
  return true;
}

export default AttributeMappingModal;
